import axios from "axios";
import React, { useEffect, useState } from "react";

import BuildIcon from "@material-ui/icons/Build";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Box, Button, Grid } from "@material-ui/core";

import Loader from "./Loader";
import DataTable from "./DataTable";
import DeviceDedicatedIPForm from "./DeviceDedicatedIPForm";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import ConfirmationDialog from "./ConfirmationDialog";

const DeviceDedicatedIP = (props) => {
  const [rules, setRules] = useState(null);
  const deviceSerial = props.deviceSerial || "UNIT12345";
  const [loading, setLoading] = useState(false);
  const { setError, setMessage } = props;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reconfigDialogOpen, setReconfigDialogOpen] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);

  const [openForm, setOpenForm] = useState(null);
  const [availableIps, setAvailableIps] = useState([]);

  const getRules = (deviceSerial) => {
    setLoading(true);
    axios
      .get(`/device/dedicated_ip/get_rules/${deviceSerial}`)
      .then((response) => {
        setRules(response.data);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error fetching rules");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteRule = (deviceSerial, ruleId) => {
    setLoading(true);
    axios
      .post(`/device/dedicated_ip/delete_rule/${deviceSerial}/${ruleId}`)
      .then((response) => {
        setMessage("Deleted rule successfully");
        getRules(deviceSerial);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error deleting rule");
      })
      .finally(() => {
        getAvailableIps();
      });
  };

  const refreshRule = (deviceSerial, ruleId) => {
    setLoading(true);
    axios
      .post(`/device/dedicated_ip/recheck_rule/${deviceSerial}/${ruleId}`)
      .then((response) => {
        setMessage("Rule queued for reconfiguration");
        getRules(deviceSerial);
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error reconfiguring rule");
      })
      .finally(() => {
        getAvailableIps();
      });
  };

  useEffect(() => {
    getRules(deviceSerial);

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getAvailableIps();
  }, []);

  const getAvailableIps = () => {
    setLoading(true);

    axios
      .get(`/device/port_forward/get_available_ips?ip_type=dedicated`)
      .then((response) => {
        setAvailableIps(response.data);
      })
      .catch((error) => {
        setError("Error fetching available ips");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 2 },
    { field: "inbound_ip", headerName: "Inbound IP", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    {
      field: "",
      headerName: "Actions",
      flex: 2,
      renderCell: (params) => (
        <Grid container spacing={2}>
          <Grid item>
            <Button
              startIcon={<RefreshIcon />}
              color="primary"
              variant="contained"
              onClick={(e) => {
                e.stopPropagation();
                setReconfigDialogOpen(true);
              }}
            >
              Reconfigure
            </Button>
          </Grid>
          <Grid item>
            <Button
              startIcon={<BuildIcon />}
              variant="contained"
              style={{
                backgroundColor: "#d32f2f",
                color: "#fff",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedRuleId(params.row.id);
                setDeleteDialogOpen(true);
              }}
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      ),
    },
  ];

  if (loading) return <Loader />;

  return (
    <>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedRuleId(null);
        }}
        onConfirm={() => {
          deleteRule(deviceSerial, selectedRuleId);
          setDeleteDialogOpen(false);
        }}
      />
      <ConfirmationDialog
        title="Reconfigure Rule"
        text="Are you sure you want to reconfigure the rule?"
        color="primary"
        open={reconfigDialogOpen}
        onClose={() => {
          setReconfigDialogOpen(false);
          setSelectedRuleId(null);
        }}
        onConfirm={() => {
          refreshRule(deviceSerial, selectedRuleId);
          setReconfigDialogOpen(false);
        }}
      />
      <Box>
        <Grid item>
          <Button
            onClick={() => setOpenForm(true)}
            color="primary"
            variant="contained"
            style={{ marginBottom: "5px" }}
          >
            Create New Rule
          </Button>
        </Grid>
        {openForm && (
          <DeviceDedicatedIPForm
            deviceSerial={deviceSerial}
            setError={setError}
            setMessage={setMessage}
            open={openForm}
            handleOpen={setOpenForm}
            availableIps={availableIps}
            callRules={getRules}
            callback={getAvailableIps}
          />
        )}
        {rules?.alloted_ips && (
          <DataTable
            loading={loading}
            data={rules.alloted_ips}
            columns={columns}
            metaData={{
              page: 1,
              page_size: 10,
              total: rules.alloted_ips.length,
            }}
            paginationMode="client"
          />
        )}
      </Box>
    </>
  );
};

export default DeviceDedicatedIP;
